import { rotateAnim, rotatePreview } from "../animation/rotate";
import { growAnim, growPreview } from "../animation/grow";
import { twinkleAnim, twinklePreview } from "../animation/twinkle";
import { wiggleAnim, wigglePreview } from "../animation/wiggle";
import { drawAnim, drawPreview } from "../animation/draw"; // added
import { stopManagedAnimation } from "../animation/timing";

// EARLY attribute extension: must run as soon as file loads (before/after core/image registration)
(function (wp) {
	if (!wp || !wp.hooks || !wp.blocks) {
		console.warn("[image block] wp not ready for attribute extension");
		return;
	}
	const { addFilter } = wp.hooks;

	function extendCoreImageAttributes(settings, name) {
		if (name !== "core/image") return settings;
		return {
			...settings,
			attributes: {
				...settings.attributes,
				imageType: { type: "string", default: "default" },
				svgContent: { type: "string", default: "" },
				animationType: { type: "string", default: "none" },
				animationLoopBehavior: { type: "string", default: "once" },
				animationDuration: { type: "number", default: 1 },
				animationEase: { type: "string", default: "linear" },
			},
		};
	}

	addFilter("blocks.registerBlockType", "posture/image-attributes", extendCoreImageAttributes);

	// Fallback: if our filter executed AFTER core/image already registered, patch attributes directly.
	wp.domReady(() => {
		const imageBlock = wp.blocks.getBlockType("core/image");
		if (imageBlock) {
			if (!imageBlock.attributes.imageType) {
				imageBlock.attributes.imageType = { type: "string", default: "default" };
			}
			if (!imageBlock.attributes.svgContent) {
				imageBlock.attributes.svgContent = { type: "string", default: "" };
			}
			if (!imageBlock.attributes.animationType) {
				imageBlock.attributes.animationType = { type: "string", default: "none" };
			}
			if (!imageBlock.attributes.animationLoopBehavior) {
				imageBlock.attributes.animationLoopBehavior = { type: "string", default: "infinite" };
			}
			if (!imageBlock.attributes.animationDuration) {
				imageBlock.attributes.animationDuration = { type: "number", default: 1 };
			}
			if (!imageBlock.attributes.animationEase) {
				imageBlock.attributes.animationEase = { type: "string", default: "linear" };
			}
		}
	});
})(window.wp);

export const initImage = () => {
	addImageControls();
	initFrontendSvgProcessing();
};

const addImageControls = () => {
	// Let's also check what blocks are already registered
	if (window.wp && window.wp.blocks) {
		const registeredBlocks = window.wp.blocks.getBlockTypes();
		const coreImageBlock = registeredBlocks.find((block) => block.name === "core/image");
	}

	(function (wp) {
		const { addFilter } = wp.hooks;
		const { createElement, Fragment, useEffect, useState } = wp.element;
		const { InspectorControls, MediaUpload, MediaUploadCheck } = wp.blockEditor || wp.editor;
		const { PanelBody, SelectControl } = wp.components;

		// gsap for editor previews
		let editorGsap = null;

		const loadEditorGsap = async () => {
			if (!editorGsap) {
				try {
					const module = await import("gsap");
					editorGsap = module.gsap || module.default;
				} catch (error) {
					// fallback to cdn if module not found
					return new Promise((resolve, reject) => {
						const script = document.createElement("script");
						script.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js";
						script.onload = () => {
							editorGsap = window.gsap;
							resolve(editorGsap);
						};
						script.onerror = reject;
						document.head.appendChild(script);
					});
				}
			}
			return editorGsap;
		};

		const previewAnimation = (animationType, buttonElement) => {
			if (!animationType || animationType === "none") return;

			loadEditorGsap()
				.then(() => {
					if (!editorGsap) {
						return;
					}

					// we need to find the main block editor area, not the inspector panel
					// start by looking in the editor canvas area
					let editorCanvas = document.querySelector(
						".editor-styles-wrapper, .block-editor-block-list__layout"
					);

					if (!editorCanvas) {
						return;
					}

					// look for the svg-element-mode block within the editor canvas
					let blockElement = editorCanvas.querySelector(".wp-block-image.svg-element-mode");

					if (!blockElement) {
						return;
					}

					// look for the main content svg (not UI elements like panel arrows)
					let targetElement = blockElement.querySelector("svg:not(.components-panel__arrow)");

					if (!targetElement) {
						return;
					}

					// temporarilly add animating class to block wrapper
					blockElement.classList.add("is-animating");
					// remove it after the duration is done
					const durationAttr = blockElement.getAttribute("data-animation-duration");
					const duration = durationAttr ? parseFloat(durationAttr) : 2;
					setTimeout(() => {
						blockElement.classList.remove("is-animating");
					}, (duration && duration > 0 ? duration : 2) * 1000 + 500);

					// run animation once using shared rotatePreview helper
					if (animationType === "rotate") {
						// Read raw tokens (duration seconds & ease keyword); mapping happens inside rotatePreview
						const blockWrapper = blockElement;
						const durationAttr = blockWrapper.getAttribute("data-animation-duration");
						const easeAttr = blockWrapper.getAttribute("data-animation-ease") || "linear";
						const duration = durationAttr ? parseFloat(durationAttr) : 2;
						rotatePreview(targetElement, {
							gsap: editorGsap,
							duration: !isNaN(duration) && duration > 0 ? duration : 2,
							ease: easeAttr,
						});
					} else if (animationType === "grow") {
						const blockWrapper = blockElement;
						const durationAttr = blockWrapper.getAttribute("data-animation-duration");
						const easeAttr = blockWrapper.getAttribute("data-animation-ease") || "linear";
						const duration = durationAttr ? parseFloat(durationAttr) : 2;
						growPreview(targetElement, {
							gsap: editorGsap,
							duration: !isNaN(duration) && duration > 0 ? duration : 2,
							ease: easeAttr,
							fromScale: 0,
							toScale: 1,
						});
					} else if (animationType === "twinkle") {
						const blockWrapper = blockElement;
						const durationAttr = blockWrapper.getAttribute("data-animation-duration");
						const easeAttr = blockWrapper.getAttribute("data-animation-ease") || "linear";
						const duration = durationAttr ? parseFloat(durationAttr) : 1.2;
						twinklePreview(targetElement, {
							gsap: editorGsap,
							duration: !isNaN(duration) && duration > 0 ? duration : 1.2,
							ease: easeAttr,
						});
					} else if (animationType === "wiggle") {
						const blockWrapper = blockElement;
						const durationAttr = blockWrapper.getAttribute("data-animation-duration");
						const easeAttr = blockWrapper.getAttribute("data-animation-ease") || "linear";
						const duration = durationAttr ? parseFloat(durationAttr) : 1;
						wigglePreview(targetElement, {
							gsap: editorGsap,
							duration: !isNaN(duration) && duration > 0 ? duration : 1,
							ease: easeAttr,
						});
					} else if (animationType === "draw") {
						// added
						const blockWrapper = blockElement;
						const durationAttr = blockWrapper.getAttribute("data-animation-duration");
						const easeAttr = blockWrapper.getAttribute("data-animation-ease") || "ease";
						const duration = durationAttr ? parseFloat(durationAttr) : 2;
						drawPreview(targetElement, {
							duration: !isNaN(duration) && duration > 0 ? duration : 2,
							ease: easeAttr,
						});
					}
				})
				.catch((error) => {
					console.log("GSAP loading error:", error);
				});
		};

		// attribute extension now handled early (see top of file)

		// Add a custom parser to detect SVG elements and set imageType accordingly
		addFilter(
			"blocks.getBlockAttributes",
			"theme-image/parse-svg-attributes",
			function (attributes, blockType, innerHTML) {
				if (blockType.name !== "core/image") return attributes;

				// Check if the innerHTML contains the SVG element class
				if (innerHTML && innerHTML.includes("is-svg-element")) {
					attributes.imageType = "svg-element";

					// Also try to extract animation attributes from data attributes
					const animationMatch = innerHTML.match(/data-animation="([^"]+)"/);
					const loopMatch = innerHTML.match(/data-animation-loop="([^"]+)"/);

					if (animationMatch) {
						attributes.animationType = animationMatch[1];
					}
					if (loopMatch) {
						attributes.animationLoopBehavior = loopMatch[1];
					}
				}

				return attributes;
			}
		);

		// Removed separate image-type inspector filter; consolidated into customImageBlockEdit for consistency

		// custom edit component for svg mode
		function customImageBlockEdit(BlockEdit) {
			return function (props) {
				if (props.name !== "core/image") return wp.element.createElement(BlockEdit, props);

				const { attributes, setAttributes, clientId } = props;
				let { imageType, animationType, animationLoopBehavior, animationDuration, animationEase } = attributes;

				// Local state for duration input to allow free typing before validation
				const [durationInput, setDurationInput] = useState(String(animationDuration || 1));

				// Sync local state if attribute changes externally
				useEffect(() => {
					if (String(animationDuration) !== durationInput) {
						setDurationInput(String(animationDuration));
					}
				}, [animationDuration]);

				// If imageType is undefined, try to detect if this should be an SVG element
				// by checking if the block element in the DOM has the SVG class
				if (imageType === undefined || imageType === "default") {
					const blockElement = document.querySelector(`[data-block="${clientId}"]`);
					if (blockElement && blockElement.querySelector(".is-svg-element")) {
						// Set the attributes to match what we detected
						setAttributes({
							...attributes,
							imageType: "svg-element",
						});
					}
				}

				const original = wp.element.createElement(BlockEdit, props);
				const { url, alt, id, svgContent } = attributes;
				// Expect original to be a figure element; clone and replace its children.
				const figureEl = original;
				const newChildren = [];
				if (!url) {
					newChildren.push(
						wp.element.createElement(wp.blockEditor.MediaUpload, {
							key: "media-upload-initial",
							onSelect: function (media) {
								fetch(media.url)
									.then((r) => r.text())
									.then((svgText) => {
										setAttributes({
											id: media.id,
											url: media.url,
											alt: media.alt || "",
											svgContent: svgText,
										});
									})
									.catch(() => {
										setAttributes({ id: media.id, url: media.url, alt: media.alt || "" });
									});
							},
							allowedTypes: ["image/svg+xml"],
							value: id,
							render: function ({ open }) {
								return wp.element.createElement(
									wp.components.Button,
									{ onClick: open, variant: "primary", style: { marginTop: "50px" } },
									"Select SVG File"
								);
							},
						})
					);
				} else {
					newChildren.push(
						wp.element.createElement(
							"div",
							{ key: "svg-wrapper", style: { position: "relative" } },
							svgContent
								? wp.element.createElement("div", {
										dangerouslySetInnerHTML: { __html: svgContent },
										style: { maxWidth: "100%", display: "inline-block" },
								  })
								: wp.element.createElement("img", {
										src: url,
										alt: alt,
										style: { maxWidth: "100%", height: "auto" },
								  }),
							wp.element.createElement(wp.blockEditor.MediaUpload, {
								onSelect: function (media) {
									fetch(media.url)
										.then((r) => r.text())
										.then((svgText) => {
											setAttributes({
												id: media.id,
												url: media.url,
												alt: media.alt || "",
												svgContent: svgText,
											});
										})
										.catch(() => {
											setAttributes({ id: media.id, url: media.url, alt: media.alt || "" });
										});
								},
								allowedTypes: ["image/svg+xml"],
								value: id,
								render: function ({ open }) {
									return wp.element.createElement(
										wp.components.Button,
										{ onClick: open, variant: "secondary", style: { marginTop: "10px" } },
										"Replace SVG"
									);
								},
							})
						)
					);
				}
				// Build inspector controls (always rendered so control never disappears for new blocks)
				const inspector = wp.element.createElement(
					wp.blockEditor.InspectorControls,
					null,
					wp.element.createElement(
						wp.components.PanelBody,
						{ title: "Image Settings" },
						wp.element.createElement(wp.components.SelectControl, {
							label: "Image Type",
							value: imageType,
							options: [
								{ label: "Default", value: "default" },
								{ label: "SVG Element", value: "svg-element" },
							],
							onChange: function (value) {
								setAttributes({ imageType: value });
								if (value === "svg-element") {
									setAttributes({ id: undefined, url: "", alt: "", svgContent: "" });
								}
							},
						}),
						// Only relevant when SVG mode selected
						imageType === "svg-element" &&
							wp.element.createElement(wp.components.SelectControl, {
								label: "Animation Type",
								value: animationType,
								options: [
									{ label: "None", value: "none" },
									{ label: "Rotate", value: "rotate" },
									{ label: "Grow", value: "grow" },
									{ label: "Twinkle", value: "twinkle" },
									{ label: "Wiggle", value: "wiggle" },
									{ label: "Draw", value: "draw" },
								],
								onChange: function (value) {
									setAttributes({ ...attributes, animationType: value });
								},
							}),
						imageType === "svg-element" &&
							animationType &&
							animationType !== "none" &&
							wp.element.createElement(wp.components.TextControl, {
								label: "Animation Duration (s)",
								type: "text",
								value: durationInput,
								onChange: function (value) {
									setDurationInput(value);
								},
								onBlur: function () {
									const num = parseFloat(durationInput);
									if (!isNaN(num) && num > 0) {
										setAttributes({ animationDuration: num });
									} else {
										// revert to attribute value if invalid input
										setDurationInput(String(animationDuration || 1));
									}
								},
								help: "Enter seconds (e.g. 0.5, 2, 3.25)",
							}),
						imageType === "svg-element" &&
							animationType &&
							animationType !== "none" &&
							wp.element.createElement(wp.components.SelectControl, {
								label: "Animation Easing",
								value: animationEase,
								options: [
									{ label: "Linear", value: "linear" },
									{ label: "Ease", value: "ease" },
									{ label: "Elastic", value: "elastic" },
								],
								onChange: function (value) {
									setAttributes({ animationEase: value });
								},
							}),
						imageType === "svg-element" &&
							animationType &&
							animationType !== "none" &&
							wp.element.createElement(wp.components.SelectControl, {
								label: "Animation Loop Behavior",
								value: animationLoopBehavior,
								options: [
									{ label: "Once on Entrance", value: "once" },
									{ label: "Sporadically", value: "sporadic" },
									{ label: "Infinite", value: "infinite" },
								],
								onChange: function (value) {
									setAttributes({ ...attributes, animationLoopBehavior: value });
								},
							}),
						imageType === "svg-element" &&
							animationType &&
							animationType !== "none" &&
							url &&
							wp.element.createElement(
								wp.components.Button,
								{
									variant: "secondary",
									isSmall: true,
									onClick: function (event) {
										previewAnimation(animationType, event.target);
									},
									style: { marginTop: "8px" },
								},
								"Preview Animation"
							),
						// alt text relevant for SVG too (applies to wrapper figure)
						url &&
							wp.element.createElement(wp.components.TextControl, {
								label: "Alt Text",
								value: alt,
								onChange: function (value) {
									setAttributes({ alt: value });
								},
							})
					)
				);

				if (imageType !== "svg-element") {
					// Default core/image rendering + our unified inspector
					return wp.element.createElement(wp.element.Fragment, null, original, inspector);
				}

				// Build a fresh figure root; preserve essential classes for block editor
				const baseFigureClass = (function () {
					if (figureEl && figureEl.type === "figure" && figureEl.props.className) {
						return figureEl.props.className;
					}
					return "wp-block-image";
				})();

				const svgFigure = wp.element.createElement(
					"figure",
					{
						"className": (baseFigureClass + " svg-element-mode").trim(),
						"style": {
							textAlign: "center",
							padding: "20px",
							border: "2px dashed #ddd",
							minHeight: "200px",
							cursor: "pointer",
						},
						"role": "group",
						"data-image-type": "svg-element",
						...(animationType && animationType !== "none"
							? {
									"data-animation": animationType,
									"data-animation-loop": animationLoopBehavior || "once",
									"data-animation-duration": animationDuration,
									"data-animation-ease": animationEase,
							  }
							: {}),
					},
					newChildren
				);

				return wp.element.createElement(wp.element.Fragment, null, svgFigure, inspector);
			};
		}

		addFilter("editor.BlockEdit", "theme-image/svg-custom-edit", customImageBlockEdit, 30);

		// add css class for svg elements
		function applyImageTypeClass(extraProps, blockType, attributes) {
			if (blockType.name !== "core/image") return extraProps;

			const { imageType, animationType, animationLoopBehavior, animationDuration, animationEase } = attributes;

			// Only process if imageType is explicitly set to "svg-element"
			if (imageType === "svg-element") {
				// ensure we don't duplicate the class
				const currentClasses = extraProps.className || "";
				if (!currentClasses.includes("is-svg-element")) {
					extraProps.className = (currentClasses + " is-svg-element").trim();
				}

				// Add the imageType as a data attribute so it can be read back
				extraProps["data-image-type"] = imageType;

				// Add animation data attributes when animation type is set and not "none"
				if (animationType && animationType !== "none") {
					extraProps["data-animation"] = animationType;
					extraProps["data-animation-loop"] = animationLoopBehavior || "once";
					if (animationDuration) extraProps["data-animation-duration"] = animationDuration;
					if (animationEase) extraProps["data-animation-ease"] = animationEase;
				}
			} else if (imageType === undefined) {
				// If imageType is undefined, check if we already have the class in the className
				// This handles blocks that were saved before but are being re-processed during validation
				const currentClasses = extraProps.className || "";
				if (currentClasses.includes("is-svg-element")) {
					// We have the class, so this is likely an SVG element
					// Try to add default data attributes to match what might be in the stored content
					extraProps["data-image-type"] = "svg-element";
					extraProps["data-animation"] = "rotate"; // Default from your example
					extraProps["data-animation-loop"] = "infinite";
				}
			}

			return extraProps;
		}
		addFilter("blocks.getSaveContent.extraProps", "theme-image/svg-element-class", applyImageTypeClass);

		// Also try the blocks.getSaveElement filter as a backup
		addFilter("blocks.getSaveElement", "theme-image/svg-element-save", function (element, blockType, attributes) {
			if (blockType.name !== "core/image" || !element) {
				return element;
			}

			const { imageType, animationType, animationLoopBehavior, animationDuration, animationEase } = attributes;

			// Check if this element already has the SVG class (from stored content)
			const currentClasses = element.props.className || "";
			const hasSvgClass = currentClasses.includes("is-svg-element");

			if (imageType === "svg-element" || hasSvgClass) {
				// Clone the element with our custom attributes
				const newProps = {
					...element.props,
				};

				// Ensure SVG class is present
				if (!hasSvgClass) {
					newProps.className = (currentClasses + " is-svg-element").trim();
				}

				// Add the imageType data attribute
				newProps["data-image-type"] = imageType || "svg-element";

				// Add data attributes - use stored values if attributes are defined,
				// otherwise try to preserve any existing data attributes from the element
				if (animationType && animationType !== "none") {
					newProps["data-animation"] = animationType;
					newProps["data-animation-loop"] = animationLoopBehavior || "infinite";
					if (animationDuration) newProps["data-animation-duration"] = animationDuration;
					if (animationEase) newProps["data-animation-ease"] = animationEase;
				} else if (hasSvgClass && (element.props["data-animation"] || attributes.animationType === undefined)) {
					// If we have the SVG class but no attributes (validation scenario),
					// preserve any existing data attributes or add defaults
					newProps["data-animation"] = element.props["data-animation"] || "rotate";
					newProps["data-animation-loop"] = element.props["data-animation-loop"] || "infinite";
					if (element.props["data-animation-duration"])
						newProps["data-animation-duration"] = element.props["data-animation-duration"];
					if (element.props["data-animation-ease"])
						newProps["data-animation-ease"] = element.props["data-animation-ease"];
				}

				return wp.element.createElement(element.type, newProps, element.props.children);
			}

			return element;
		});
	})(window.wp);
};

// replace img tags with inline svg on frontend
const initFrontendSvgProcessing = () => {
	const isInEditor =
		document.querySelector(".block-editor") ||
		document.querySelector(".edit-post-visual-editor") ||
		document.querySelector(".editor-styles-wrapper") ||
		document.body.classList.contains("block-editor-page") ||
		document.body.classList.contains("post-php") ||
		document.body.classList.contains("post-new-php") ||
		window.location.pathname.includes("/wp-admin/");

	if (isInEditor) return;

	// import gsap dynamically
	let gsap = null;
	let animatedElements = new Map(); // track animated elements and their animations

	const loadGsap = async () => {
		if (!gsap) {
			try {
				const module = await import("gsap");
				gsap = module.gsap || module.default;
			} catch (error) {
				// fallback to cdn if module not found
				return new Promise((resolve, reject) => {
					const script = document.createElement("script");
					script.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js";
					script.onload = () => {
						gsap = window.gsap;
						resolve(gsap);
					};
					script.onerror = reject;
					document.head.appendChild(script);
				});
			}
		}
		return gsap;
	};

	// intersection observer for viewport-based animations
	let intersectionObserver = null;
	// track timeouts for sporadic animations separately
	let sporadicTimers = new Map();

	const createIntersectionObserver = () => {
		if (!window.IntersectionObserver) return null;

		return new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const svgElement = entry.target;
					const animationType = svgElement.getAttribute("data-animation-type");
					const loopBehavior = svgElement.getAttribute("data-animation-loop") || "infinite";

					if (entry.isIntersecting) {
						// start animation when in view
						if (animationType && animationType !== "none" && !animatedElements.has(svgElement)) {
							startAnimation(svgElement, animationType, loopBehavior);
						}
					} else {
						// pause/kill animation when out of view (except for "once" which should complete)
						if (loopBehavior !== "once") {
							stopAnimation(svgElement);
						}
					}
				});
			},
			{
				rootMargin: "50px", // start animation 50px before entering viewport
				threshold: 0.1, // trigger when 10% visible
			}
		);
	};

	function startAnimation(svgElement, animationType, loopBehavior = "infinite") {
		if (!gsap || animatedElements.has(svgElement)) return;
		const durationAttr = parseFloat(svgElement.getAttribute("data-animation-duration"));
		const easeAttr = svgElement.getAttribute("data-animation-ease") || "linear";
		const effectiveDuration = !isNaN(durationAttr) && durationAttr > 0 ? durationAttr : 2;

		switch (animationType) {
			case "rotate":
				rotateAnim(svgElement, {
					gsap,
					loopBehavior,
					animatedElements,
					sporadicTimers,
					duration: effectiveDuration,
					ease: easeAttr,
				});
				break;
			case "grow":
				growAnim(svgElement, {
					gsap,
					loopBehavior,
					animatedElements,
					sporadicTimers,
					duration: effectiveDuration,
					ease: easeAttr,
					fromScale: 0,
					toScale: 1,
				});
				break;
			case "twinkle":
				twinkleAnim(svgElement, {
					gsap,
					loopBehavior,
					animatedElements,
					sporadicTimers,
					duration: effectiveDuration,
					ease: easeAttr,
				});
				break;
			case "wiggle":
				wiggleAnim(svgElement, {
					gsap,
					loopBehavior,
					animatedElements,
					sporadicTimers,
					duration: effectiveDuration,
					ease: easeAttr,
				});
				break;
			case "draw": // added
				// drawAnim ignores gsap animations internally but we pass signature for consistency
				drawAnim(svgElement, {
					gsap,
					loopBehavior,
					animatedElements,
					sporadicTimers,
					duration: effectiveDuration,
					ease: easeAttr,
				});
				break;
			// future animation types: scale, pulse, etc.
		}
	}

	function stopAnimation(svgElement) {
		const blockRoot = svgElement.closest(".wp-block-image.svg-element-mode");
	}

	function applyAnimation(svgElement, animationType, loopBehavior = "infinite") {
		if (!animationType || animationType === "none") return;

		loadGsap()
			.then(() => {
				if (!gsap) return;

				// store animation type and loop behavior on element for intersection observer
				svgElement.setAttribute("data-animation-type", animationType);
				svgElement.setAttribute("data-animation-loop", loopBehavior);

				// create intersection observer if it doesn't exist
				if (!intersectionObserver) {
					intersectionObserver = createIntersectionObserver();
				}

				// start observing this element
				if (intersectionObserver) {
					intersectionObserver.observe(svgElement);
				} else {
					// fallback for browsers without intersection observer
					startAnimation(svgElement, animationType, loopBehavior);
				}
			})
			.catch((error) => {
				// silently fail if gsap cannot be loaded
			});
	}

	function processSvgElements() {
		const svgElements = document.querySelectorAll(".wp-block-image.is-svg-element img");

		svgElements.forEach(function (img) {
			if (img.hasAttribute("data-svg-processed")) return;
			img.setAttribute("data-svg-processed", "true");

			const svgUrl = img.src;
			if (!svgUrl || !svgUrl.includes(".svg")) return;

			const blockContainer = img.closest(".wp-block-image.is-svg-element");
			const animationType = blockContainer ? blockContainer.getAttribute("data-animation") : null;
			const animationLoop = blockContainer ? blockContainer.getAttribute("data-animation-loop") : "infinite";

			fetch(svgUrl)
				.then(function (response) {
					return response.text();
				})
				.then(function (svgText) {
					const tempDiv = document.createElement("div");
					tempDiv.innerHTML = svgText;
					const svgElement = tempDiv.querySelector("svg");

					if (svgElement) {
						if (img.alt) {
							svgElement.setAttribute("aria-label", img.alt);
							svgElement.setAttribute("role", "img");
						}

						svgElement.classList.add("inline-svg-element");

						// Copy animation-related data attributes from block container for direct access
						if (blockContainer) {
							[
								"data-animation",
								"data-animation-loop",
								"data-animation-duration",
								"data-animation-ease",
							].forEach((attr) => {
								if (blockContainer.hasAttribute(attr)) {
									svgElement.setAttribute(attr, blockContainer.getAttribute(attr));
								}
							});
						}
						img.parentNode.replaceChild(svgElement, img);

						// apply animation if specified
						if (animationType && animationType !== "none") {
							applyAnimation(svgElement, animationType, animationLoop);
						}
					}
				})
				.catch(function (error) {
					// silently fail
				});
		});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", processSvgElements);
	} else {
		processSvgElements();
	}

	// watch for dynamically added content
	if (window.MutationObserver) {
		let processingTimeout;

		const observer = new MutationObserver(function (mutations) {
			let shouldProcess = false;

			mutations.forEach(function (mutation) {
				if (mutation.addedNodes.length) {
					for (let i = 0; i < mutation.addedNodes.length; i++) {
						const node = mutation.addedNodes[i];
						if (node.nodeType === 1) {
							const hasSvgElements =
								node.querySelector &&
								node.querySelector(".wp-block-image.is-svg-element img:not([data-svg-processed])");
							if (hasSvgElements) {
								shouldProcess = true;
								break;
							}
						}
					}
				}
			});

			if (shouldProcess) {
				clearTimeout(processingTimeout);
				processingTimeout = setTimeout(processSvgElements, 500);
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: false,
			characterData: false,
		});
	}
};
